import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { imageUrl } from "../config";
import { useLang } from "../i18n";

const STATIC = "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images";

const ITEMS = [
  { label: "Yapay Kaplama", to: "/kaplama-yapay", image: `${STATIC}/4979c6ff562122788f7f9a6d836a8061b854e40ece134025cf9b2f60b7e60a5b.jpeg` },
  { label: "Doğal Kaplama", to: "/kaplama", image: `${STATIC}/f3e044845407aebb4bf5364e3238279998f14047f0461a5aa2f660beb3d34cb7.jpeg` },
  { label: "Boyalı Kaplama", to: "/boyali-kaplama", image: `${STATIC}/de233e4bac11161e0e3fbaa70ab91c038e473621aae8f045a43bd01d8c30d269.jpeg` },
  { label: "Kök Kaplama", to: "/kaplama/kok", image: `${STATIC}/a5318f7195abc1563e8ea4d325a3bbb166178edcad2b777e39fc5a9cb1252c7b.jpeg` },
  { label: "Kereste", to: "/kereste", image: "https://images.unsplash.com/photo-1634672652995-ee7525bce595?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Vinterio", to: "/vinterio", image: `${STATIC}/be5dbfbc38f066b02080428578a1dfcb0cebc2d496a3576ab295e2e79fb2f350.jpeg` },
  { label: "Linea", to: "/linea", image: `${STATIC}/a12c2bf84d6ae8af9611926f9dabea466e50ba08e3268b047b0195c0612277a5.jpeg` },
  { label: "Kontrplak", to: "/kontrplak", image: "https://images.pexels.com/photos/5089122/pexels-photo-5089122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { label: "Piramit Kaplama", to: "/kaplama/piramit", image: `${STATIC}/89262a3dd4120ea918c69b26546f676a7aade4c5f35b79081e6f2d71ce860adf.jpeg` },
  { label: "3D Yüzey", to: "/kaplama-yapay/3d", image: `${STATIC}/c9cb3fa1a0e2d0b24ab8699e853065fd97fc210fb4b1c1879ea05cfdd73bd894.jpeg` },
];

export const HomeCategorySlider = () => {
  const { dn } = useLang();
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: "smooth" });
  };

  return (
    <section id="urun-gruplari" data-testid="home-category-slider" className="relative pt-10 md:pt-12 pb-24 md:pb-28 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-end justify-between mb-10 md:mb-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#7BA05B]" />
            <span className="font-sans uppercase tracking-[0.3em] text-[10px] text-[#C67D57]">İnter Orman</span>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tighter text-[#1A1715]">
            {dn("Ürünlerimiz")}
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            data-testid="slider-prev"
            onClick={() => scrollBy(-1)}
            aria-label="Geri"
            className="w-11 h-11 rounded-full border border-black/20 flex items-center justify-center text-[#1A1715] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300"
          >
            <ArrowLeft size={17} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            data-testid="slider-next"
            onClick={() => scrollBy(1)}
            aria-label="İleri"
            className="w-11 h-11 rounded-full border border-black/20 flex items-center justify-center text-[#1A1715] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300"
          >
            <ArrowRight size={17} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        data-testid="slider-track"
        className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory px-6 md:px-12 pb-4 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        {ITEMS.map((item, i) => (
          <motion.div
            key={item.to + item.label}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: Math.min(i, 4) * 0.06 }}
            className="shrink-0 snap-start"
          >
            <Link
              to={item.to}
              data-testid={`slider-card-${item.to.replace(/\//g, "-").slice(1)}`}
              className="group relative block w-[300px] md:w-[380px] aspect-[3/4] rounded-lg overflow-hidden bg-[#F0EDE8]"
            >
              <img
                src={item.image.startsWith("/api/") ? imageUrl(item.image) : item.image}
                alt={item.label}
                loading={i < 4 ? "eager" : "lazy"}
                fetchPriority={i < 4 ? "high" : "auto"}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/95 via-[#0D0C0B]/35 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="block h-px w-8 bg-[#7BA05B] mb-3 group-hover:w-12 transition-all duration-300" />
                <h3 className="font-serif text-2xl md:text-[1.7rem] font-medium text-white tracking-tight leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
                  {dn(item.label)}
                </h3>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
